/** @module Choices for free text fields: what the user wrote before plus a few presets. */
import { useQuery } from "@tanstack/react-query";

import { api, problemFrom } from "@/api/client";

async function fetchSuggestions() {
  const { data, response } = await api.GET("/api/v1/collection/suggestions", {});
  if (!data) throw problemFrom(response);
  return data;
}

/** Earlier entries of "Gekauft bei" and notes, most used first. */
export function useSuggestions() {
  // Refetched on every open: a shop typed a minute ago belongs in the list now.
  return useQuery({ queryKey: ["suggestions"], queryFn: fetchSuggestions });
}

export interface OptionGroup {
  label: string;
  options: string[];
}

/** Drop empty groups and every value an earlier group already offers, ignoring case. */
export function mergeGroups(groups: OptionGroup[]): OptionGroup[] {
  const seen = new Set<string>();
  return groups
    .map((group) => ({
      label: group.label,
      options: group.options.filter((option) => {
        const key = option.trim().toLocaleLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    }))
    .filter((group) => group.options.length > 0);
}

/** The options containing the typed text; all of them while nothing is typed. */
export function filterGroups(groups: OptionGroup[], text: string | null): OptionGroup[] {
  const needle = (text ?? "").trim().toLocaleLowerCase();
  if (!needle) return groups;
  return groups
    .map((group) => ({
      label: group.label,
      options: group.options.filter((option) => option.toLocaleLowerCase().includes(needle)),
    }))
    .filter((group) => group.options.length > 0);
}

/**
 * Names for a new place: each base word with the next free number ("Box 3" after Box 1
 * and Box 2), so boxes get counted without anyone keeping track.
 */
export function numberedNames(bases: string[], existing: string[]): string[] {
  return bases.map((base) => {
    const pattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+(\\d+)$`, "i");
    const numbers = existing
      .map((name) => pattern.exec(name.trim())?.[1])
      .filter((value): value is string => value !== undefined)
      .map(Number);
    return `${base} ${String(numbers.length ? Math.max(...numbers) + 1 : 1)}`;
  });
}
